import { create } from "zustand";
import { api, FilterMode, Task, TaskStats } from "../lib/tauri";

interface TaskState {
  tasks: Task[];
  allTasks: Task[];
  stats: TaskStats;
  filter: FilterMode;
  search: string;
  loading: boolean;
  lastDeleted: Task | null;
  load: () => Promise<void>;
  setFilter: (filter: FilterMode) => Promise<void>;
  setSearch: (search: string) => Promise<void>;
  createTask: (input: { title: string; description?: string; priority?: string; dueDate?: string | null; projectId?: string; repeatRule?: string; goalId?: string }) => Promise<void>;
  updateTask: (id: string, patch: Record<string, unknown>) => Promise<void>;
  toggleTask: (id: string) => Promise<void>;
  deleteTask: (task: Task) => Promise<void>;
  restoreTask: (task: Task) => Promise<void>;
  permanentlyDelete: (id: string) => Promise<void>;
  undoDelete: () => Promise<void>;
}

const emptyStats = { active: 0, completed: 0, today: 0, high: 0, total: 0 };

export const useTaskStore = create<TaskState>((set, get) => ({
  tasks: [],
  allTasks: [],
  stats: emptyStats,
  filter: "active",
  search: "",
  loading: false,
  lastDeleted: null,
  load: async () => {
    set({ loading: true });
    const { filter, search } = get();
    const [tasks, allTasks, stats] = await Promise.all([
      api.listTasks(filter, search || undefined),
      api.listTasks("all"),
      api.stats(),
    ]);
    set({ tasks, allTasks, stats, loading: false });
  },
  setFilter: async (filter) => {
    set({ filter });
    await get().load();
  },
  setSearch: async (search) => {
    set({ search });
    await get().load();
  },
  createTask: async (input) => {
    await api.createTask(input as never);
    await get().load();
  },
  updateTask: async (id, patch) => {
    await api.updateTask(id, patch);
    await get().load();
  },
  toggleTask: async (id) => {
    await api.toggleTask(id);
    await get().load();
  },
  deleteTask: async (task) => {
    await api.deleteTask(task.id);
    set({ lastDeleted: task });
    await get().load();
  },
  restoreTask: async (task) => {
    await api.restoreTask(task);
    await get().load();
  },
  permanentlyDelete: async (id) => {
    const perm = (api as { permanentlyDeleteTask?: (id: string) => Promise<void> }).permanentlyDeleteTask;
    if (perm) {
      await perm(id);
    } else {
      // Desktop Tauri fallback: no-op (could be wired to a Rust command later)
      console.warn("permanentlyDeleteTask not supported in this environment");
    }
    await get().load();
  },
  undoDelete: async () => {
    const task = get().lastDeleted;
    if (!task) return;
    await api.restoreTask(task);
    set({ lastDeleted: null });
    await get().load();
  },
}));
